/**
 * Performance Promo Links — apply the active promo code to hand-authored Criterion links.
 *
 * PerformanceModule::markPromoLinks() tags eligible anchors at render time with the promo-ready
 * form of their own URL, holding a placeholder where the code goes. That decision — is this anchor
 * a Criterion production link? — is identical for every visitor, so it lands once in the cached
 * HTML. All that's left here is substituting the code, which is why this file parses nothing.
 *
 * The anchor's own href stays the un-promoed URL throughout, so it is always correct to click
 * (before hydration, and after a code is removed) and there is nothing to reconstruct on clear.
 */

const { hooks, register } = window.sitchco;
const { promo, hooks: HOOKS } = window.sitchco.performance;

const SELECTOR = '[data-promo-link]';

/** Each marked anchor's original, un-promoed href — what "remove code" restores. */
const originals = new WeakMap();

function originalHref(anchor) {
    if (!originals.has(anchor)) {
        originals.set(anchor, anchor.getAttribute('href') ?? '');
    }
    return originals.get(anchor);
}

/**
 * Carry whatever query and fragment the live href has onto a replacement.
 *
 * @sitchco/datalayer decorates outbound Criterion links with attribution params (utm_*, crit_source,
 * vwo_q), and it re-decorates on added nodes only — an attribute rewrite goes unnoticed. Replacing
 * the href wholesale would therefore drop the attribution for exactly the promo visitors a campaign
 * is measuring. Existing params on the replacement win, so the promo path is never overwritten.
 */
function carryParams(href, anchor) {
    const live = new URL(anchor.href, window.location.href);
    const next = new URL(href, window.location.href);
    live.searchParams.forEach((value, key) => {
        if (!next.searchParams.has(key)) {
            next.searchParams.set(key, value);
        }
    });

    if (!next.hash) {
        next.hash = live.hash;
    }
    return next.toString();
}

function apply(code) {
    document.querySelectorAll(SELECTOR).forEach((anchor) => {
        const base = originalHref(anchor);
        const promoted = promo.applyToTemplate(anchor.dataset.promoLink, code);
        const href = promoted || base;
        if (href) {
            anchor.setAttribute('href', carryParams(href, anchor));
        }
    });
}

register(() => {
    if (!document.querySelector(SELECTOR)) {
        return;
    }

    // Awaited rather than read synchronously: a ?promo_code= arrival has no other validation on a
    // page with no performance block, and publishing a /promo/{code} link for a code the API
    // rejected is worse than being a beat late.
    promo.ready().then(apply);
    hooks.addAction(HOOKS.PROMO_CHANGE, ({ code }) => apply(code), 10, 'performance-promo-links');
    // The cookie path resolves without validation, so an expired code can be live in every href by
    // the time a block's fetch reports it invalid. That report reaches clearCode(), which can't fire
    // PROMO_CHANGE from inside a DATA_LOAD handler — hence this second subscription.
    hooks.addAction(HOOKS.PROMO_CLEARED, ({ code }) => apply(code), 10, 'performance-promo-links');
});
